import { Injectable } from "@angular/core";
import { Observable, Subject, from } from "rxjs";
import { GeoResponse } from "src/app/interfaces/geo-interface";
import { Geolocation } from "@capacitor/geolocation";

import {
  distinctUntilChanged,
  map,
  filter,
  takeUntil,
  switchMap,
} from "rxjs/operators";
import { Navigator } from "src/app/components/menu/navigator";
import { Platform } from "@ionic/angular";
import { WebservicesService } from "./webservices/webservices.service";
import { UserSessionService } from "./user-session/user-session.service";
import { ConfigService } from "./config/config.service";
import { EventsService } from "./event/event.service";
import { JurisdictionService } from "./geo/jurisdiction.service";

@Injectable({
  providedIn: "root",
})
export class GoogleGeoService {
  private _jurisdictionSubject: Subject<GeoResponse> =
    new Subject<GeoResponse>();
  private customerSubscription: any;
  private customerId: string;
  private destroy$ = new Subject<void>();
  private geoEnforcedCheck = false;
  private errorObject: any = null;
  private lastGeoResponse: any;

  constructor(
    private webservices: WebservicesService,
    private userSession: UserSessionService,
    private config: ConfigService,
    private events: EventsService,
    private navigator: Navigator,
    private platform: Platform,
    private jurisdiction: JurisdictionService
  ) {}
  public async initGeoVerificationProcess() {
    this.events.subscribe("event:geoComply:Ready", () => {
      if (this.errorObject) {
        console.log("Sending Error Object", this.errorObject.error);
        this.events.publish("event:googleGeo:Error", this.errorObject.error);
      }
    });

    this.geoEnforcedCheck =
      this.config.getConfig()?.GEOENFORCED == "true" ?? false;
    await new Promise((resolve, reject) => {
      this.getCustomerObservable()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: async (customerId) => {
            this.customerId = customerId;
            try {
              await this.getDeviceLocation();
              resolve(1);
            } catch (error) {
              if (error) console.error(error);
              if (!this.platform.is("capacitor") && error.code == "1") {
                this.errorObject = this.createErrorObject(
                  "GOOGLEGEOUSERERROR",
                  "User denied location permission"
                );
              } else if (this.platform.is("capacitor") && error.code == "2") {
                this.errorObject = this.createErrorObject(
                  "GOOGLEGEOERROR",
                  "Location Permission Denied"
                );
              } else {
                this.errorObject = this.createErrorObject(
                  "GOOGLEGEOERROR",
                  "Geolocation not available"
                );
              }

              this.events.publish("event:geoComply:init");
              console.log("Google Geo Error", this.errorObject.error);
              reject(this.errorObject);
            }
          },
          error: (err) => {
            console.error("Error in customer subscription", err);
            reject(err);
          },
        });
    });
  }

  private async getDeviceLocation() {
    if (this.platform.is("capacitor") || this.platform.is("cordova")) {
      return await this.getMobileLocation();
    } else {
      return await this.getWebLocation();
    }
  }
  private async getMobileLocation() {
    try {
      const permissions = await Geolocation.requestPermissions();
      if (permissions.location !== "granted") {
        throw { code: "2", message: "Location Permission Denied" };
      }

      const coords = await Geolocation.getCurrentPosition();
      return this.verifyGeolocation(coords.coords);
    } catch (error) {
      throw error;
    }
  }

  private async getWebLocation() {
    if (navigator.geolocation) {
      try {
        const position: {
          coords: {
            latitude: number;
            longitude: number;
          };
        } = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 0,
          });
        });

        if (position?.coords) {
          return this.verifyGeolocation(position?.coords);
        } else {
          throw Error("Geolocation not available");
        }
      } catch (error) {
        throw error;
      }
    } else {
      return Error("Geolocation not available");
    }
  }

  private getCustomerObservable(): Observable<string> {
    return from(this.userSession.getCustomerSubscription()).pipe(
      switchMap((behaviorSubject) => behaviorSubject.asObservable()),
      filter((customer) => !!customer),
      map((customer) => customer.customerId.toString()),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    );
  }

  public verifyGeolocation(coords): void {
    const { latitude, longitude } = coords;

    this.postGeolocationData(
      this.customerId,
      this.userSession.getSessionToken(),
      latitude,
      longitude
    );
  }

  private postGeolocationData(
    customerId: string,
    sessionToken: string,
    latitude: number,
    longitude: number
  ): void {
    const postData = {
      clientapp: this.config.getClientApp(),
      customer_id: customerId,
      session_token: sessionToken,
      data: this.getPositionJSON({ latitude, longitude })
    };

    this.webservices.post("geolocation/boundary-check", postData).subscribe({
      next: (response: any) => {
        console.log("GeoResponse", response);
        if (response.result === 1) {
          this.lastGeoResponse = response?.payload?.geo_result;
          if (
            (response.payload &&
              response.payload.geo_result.is_within_border == true) ||
            (this.geoEnforcedCheck &&
              response.payload.geo_result.geolocation_enforced == true)
          ) {
            // return this.handleGeolocationWithinBorder();
          } else if (
            response.payload &&
            response.payload.geo_result.is_allowed == false
          ) {
            console.error("Location not allowed. Sending error response.");
            this.errorObject = {
              ...this.createErrorObject(
                "GOOGLEGEOBOUNDARYERROR",
                "Location not allowed. Sending error response."
              ),
              ...response.payload?.geo_result,
            };
            console.log(
              "Location Not Allowed Sending GeoResponse",
              this.errorObject.error
            );
            this.events.publish("event:geoComply:init");
          } else {
            console.log(
              "Location Allowed Sending GeoResponse",
              response?.payload?.geo_result
            );
            this._jurisdictionSubject.next(response?.payload?.geo_result);
          }
        } else {
          console.error("Unexpected response result:", response.result);
        }
      },
      error: (error) => {
        this.errorObject = this.createErrorObject("GOOGLEGEOERROR", error);
        this.events.publish("event:geoComply:init");
      },
    });
  }

  private getPositionJSON(position: {
    latitude: number;
    longitude: number;
  }): string {
    return JSON.stringify({
      coords: {
        latitude: position.latitude,
        longitude: position.longitude,
      },
    });
  }
  private handleGeolocationWithinBorder() {
    this.jurisdiction.verifyJurisdiction(() => {});
  }
  public getJurisdictionUpdates(): Observable<GeoResponse> {
    return this._jurisdictionSubject.asObservable();
  }
  public safeToPlay() {
    if (this.jurisdiction.isAllowedJurisdiction()) {
      return true;
    } else if (this.lastGeoResponse?.is_allowed) {
      return true;
    } else if (this.lastGeoResponse?.is_allowed === false) {
      return false;
    }
    return false;
  }
  public sendGeoError() {
    if (this.jurisdiction.getCachedGeoResponse()) {
      return this.jurisdiction.emitGeoResponseFromCache();
    }
    if (!this.errorObject) {
      if (this.platform.is("capacitor")) {
        this.checkMobileGeolocationPermission().catch((error) => {
          this.errorObject = this.createErrorObject(
            "GOOGLEGEOERROR",
            "User denied location permission"
          );
          console.log("Geolocation not available", this.errorObject.error);
          this.events.publish("event:googleGeo:Error", this.errorObject.error);
        });
      } else {
        this.checkWebGeolocationPermission().catch((error) => {
          this.errorObject = this.createErrorObject(
            "GOOGLEGEOUSERERROR",
            "User denied location permission"
          );
          console.log(
            "User Denied Location Permission",
            this.errorObject.error
          );
          this.events.publish("event:googleGeo:Error", this.errorObject.error);
        });
      }
    } else {
      console.log("Sending Error Object", this.errorObject.error);
      this.events.publish("event:googleGeo:Error", this.errorObject.error);
    }
  }
  checkWebGeolocationPermission() {
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve(position);
          },
          (error) => {
            if (error.code == 1) {
              reject(error);
            } else {
              reject(error);
            }
          }
        );
      } else {
        reject("Geolocation not available");
      }
    });
  }
  checkMobileGeolocationPermission() {
    return new Promise((resolve, reject) => {
      Geolocation.getCurrentPosition()
        .then((position) => {
          resolve(position);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
  createErrorObject(code = "", message = "") {
    return {
      error: {
        error_code: code,
        error_message: message,
        address: null,
        is_allowed: false,
      },
    };
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
